// =====================================================================
// Receituario (Receituário / S55) — GESTÃO da biblioteca de fórmulas do médico.
// Página /prontuario/receituario (botão no Prontuário). O médico escolhe o
// profissional, organiza fórmulas em PASTAS e cria/edita/exclui itens. Depois
// puxa esses itens na hora de emitir a Receita (picker no EmittedDocuments).
//
// Multi-tenant: backend filtra por companyId do JWT (LGPD).
// professionalId = clinic_professionals.id (mesmo dropdown da consulta/Receita).
// =====================================================================
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  CircularProgress,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  Box,
  IconButton,
  Tooltip,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import FolderOutlinedIcon from "@material-ui/icons/FolderOutlined";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PublishIcon from "@material-ui/icons/Publish";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";

import { listEncounterProfessionals } from "../../services/encounterService";
import {
  listPrescriptionItems,
  createPrescriptionItem,
  updatePrescriptionItem,
  deletePrescriptionItem,
  bulkCreatePrescriptionItems,
  prescriptionErrorMessage
} from "../../services/prescriptionService";
import useIsMobile from "../../utils/useIsMobile";

const SHARED = "__shared__"; // pseudo-valor do seletor = itens compartilhados (professional_id NULL)

const useStyles = makeStyles((theme) => ({
  mainPaper: { flex: 1, padding: theme.spacing(2), overflowY: "auto", minHeight: 0 },
  toolbar: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2)
  },
  selector: { flexGrow: 1, minWidth: 130 },
  searchField: { flexGrow: 1, minWidth: 130 },
  loadingWrap: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 },
  emptyState: { padding: theme.spacing(6), textAlign: "center", color: theme.palette.text.secondary },
  folderRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    cursor: "pointer",
    padding: theme.spacing(1, 0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    userSelect: "none",
    "&:hover": { background: theme.palette.action.hover }
  },
  folderName: { fontWeight: 600, flex: 1, minWidth: 0 },
  itemRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1, 0.75, 4),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  itemText: { flex: 1, minWidth: 0 },
  itemName: { wordBreak: "break-word" },
  itemMeta: { color: theme.palette.text.secondary, fontSize: 12, marginTop: 1, wordBreak: "break-word" },
  itemActions: { flexShrink: 0 },
  dialogFields: { display: "flex", flexDirection: "column", gap: theme.spacing(1), marginTop: theme.spacing(0.5) }
}));

const norm = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .replace(/[áàâãä]/g, "a")
    .replace(/[éèêë]/g, "e")
    .replace(/[íìîï]/g, "i")
    .replace(/[óòôõö]/g, "o")
    .replace(/[úùûü]/g, "u")
    .replace(/ç/g, "c")
    .replace(/ñ/g, "n");

// "Nome de exibição (nome curto)" -> { displayName, shortName }
const parseLine = (raw) => {
  const line = (raw || "").trim();
  if (!line) return null;
  const m = line.match(/^(.*?)\s*\(([^()]*)\)\s*$/);
  if (m && m[1].trim()) return { displayName: m[1].trim(), shortName: m[2].trim() || null };
  return { displayName: line, shortName: null };
};

const EMPTY_FORM = { id: null, folder: "", displayName: "", shortName: "", posology: "", body: "", notes: "" };

const Receituario = () => {
  const classes = useStyles();
  const history = useHistory();
  const isMobile = useIsMobile();

  const [professionals, setProfessionals] = useState([]);
  const [profId, setProfId] = useState(""); // "" = nenhum; id string; SHARED
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [openFolders, setOpenFolders] = useState({});

  const [form, setForm] = useState(EMPTY_FORM);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [importOpen, setImportOpen] = useState(false);
  const [importFolder, setImportFolder] = useState("");
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);

  const isShared = profId === SHARED;
  const hasScope = profId !== "";

  // carrega profissionais (dropdown)
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await listEncounterProfessionals();
        if (active) setProfessionals((r && r.professionals) || []);
      } catch (e) {
        /* best-effort */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const fetchItems = useCallback(async () => {
    if (profId === "") {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      let data;
      if (isShared) {
        // só compartilhados: traz tudo e filtra professional_id null no cliente
        data = await listPrescriptionItems({ onlyActive: false });
        const all = Array.isArray(data && data.items) ? data.items : [];
        setItems(all.filter((it) => it.professionalId === null || it.professionalId === undefined));
      } else {
        data = await listPrescriptionItems({
          professionalId: profId,
          includeShared: false,
          onlyActive: false
        });
        setItems(Array.isArray(data && data.items) ? data.items : []);
      }
    } catch (err) {
      toast.error(prescriptionErrorMessage(err));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [profId, isShared]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // filtra + agrupa por pasta
  const { folders, byFolder, total } = useMemo(() => {
    const q = norm(search.trim());
    const filtered = q
      ? items.filter(
          (it) =>
            norm(it.displayName).includes(q) ||
            norm(it.shortName).includes(q) ||
            norm(it.folder).includes(q)
        )
      : items;
    const map = {};
    filtered.forEach((it) => {
      const f = it.folder || "Geral";
      if (!map[f]) map[f] = [];
      map[f].push(it);
    });
    const fl = Object.keys(map).sort((a, b) => {
      if (a === "Geral") return -1;
      if (b === "Geral") return 1;
      return a.localeCompare(b);
    });
    return { folders: fl, byFolder: map, total: filtered.length };
  }, [items, search]);

  const isSearching = !!search.trim();
  const toggleFolder = (f) => setOpenFolders((prev) => ({ ...prev, [f]: !prev[f] }));

  const existingFolders = useMemo(
    () => Array.from(new Set(items.map((it) => it.folder).filter(Boolean))).sort(),
    [items]
  );

  const scopeProfessionalId = () => (isShared ? null : Number(profId));

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };
  const openEdit = (it) => {
    setForm({
      id: it.id,
      folder: it.folder || "",
      displayName: it.displayName || "",
      shortName: it.shortName || "",
      posology: it.posology || "",
      body: it.body || "",
      notes: it.notes || ""
    });
    setDialogOpen(true);
  };

  const setF = (k) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [k]: value }));
  };

  const handleSave = async () => {
    if (!form.displayName.trim()) {
      toast.error("Informe o nome do item.");
      return;
    }
    setSaving(true);
    const payload = {
      professionalId: scopeProfessionalId(),
      folder: form.folder.trim() || null,
      displayName: form.displayName.trim(),
      shortName: form.shortName.trim() || null,
      posology: form.posology.trim() || null,
      body: form.body.trim() || null,
      notes: form.notes.trim() || null
    };
    try {
      if (form.id) await updatePrescriptionItem(form.id, payload);
      else await createPrescriptionItem(payload);
      toast.success(form.id ? "Item atualizado." : "Item adicionado.");
      setDialogOpen(false);
      await fetchItems();
    } catch (err) {
      toast.error(prescriptionErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    const it = deleteTarget;
    setDeleteTarget(null);
    if (!it) return;
    try {
      await deletePrescriptionItem(it.id);
      toast.success("Item removido.");
      await fetchItems();
    } catch (err) {
      toast.error(prescriptionErrorMessage(err));
    }
  };

  const handleImport = async () => {
    const parsed = importText
      .split("\n")
      .map(parseLine)
      .filter(Boolean);
    if (parsed.length === 0) {
      toast.error("Cole ao menos um item (um por linha).");
      return;
    }
    setImporting(true);
    try {
      const r = await bulkCreatePrescriptionItems({
        professionalId: scopeProfessionalId(),
        folder: importFolder.trim() || null,
        items: parsed
      });
      toast.success(`${(r && r.created) || 0} item(ns) importado(s).`);
      setImportOpen(false);
      setImportText("");
      setImportFolder("");
      await fetchItems();
    } catch (err) {
      toast.error(prescriptionErrorMessage(err));
    } finally {
      setImporting(false);
    }
  };

  const scopeLabel = isShared
    ? "Compartilhado (clínica)"
    : (professionals.find((p) => String(p.id) === String(profId)) || {}).name || "";

  return (
    <MainContainer>
      <MainHeader>
        <Tooltip title="Voltar ao Prontuário">
          <IconButton size="small" onClick={() => history.push("/prontuario")} style={{ marginRight: 8 }}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Title>Receituário</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PublishIcon />}
            disabled={!hasScope}
            onClick={() => setImportOpen(true)}
          >
            Importar lista
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            disabled={!hasScope}
            onClick={openCreate}
          >
            Novo item
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.toolbar}>
          <TextField
            select
            className={classes.selector}
            label="Receituário de"
            value={profId}
            onChange={(e) => setProfId(e.target.value)}
            variant="outlined"
            size="small"
          >
            <MenuItem value="">Selecione o profissional…</MenuItem>
            {professionals.map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>
                {p.name}
              </MenuItem>
            ))}
            <MenuItem value={SHARED}>Compartilhado (clínica)</MenuItem>
          </TextField>

          <TextField
            className={classes.searchField}
            placeholder="Buscar item ou pasta..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            disabled={!hasScope}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          <Box flexGrow={1} />
          {hasScope && (
            <Typography variant="body2" color="textSecondary">
              {total} {total === 1 ? "item" : "itens"}
            </Typography>
          )}
        </div>

        {!hasScope ? (
          <div className={classes.emptyState}>
            <Typography variant="h6" gutterBottom>
              Selecione o profissional
            </Typography>
            <Typography variant="body2">
              Escolha de quem é o receituário pra ver, organizar e cadastrar as fórmulas em pastas.
            </Typography>
          </div>
        ) : loading ? (
          <div className={classes.loadingWrap}>
            <CircularProgress />
          </div>
        ) : total === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="h6" gutterBottom>
              {items.length === 0 ? "Receituário vazio" : "Nenhum resultado"}
            </Typography>
            <Typography variant="body2">
              {items.length === 0
                ? `Nenhuma fórmula cadastrada para ${scopeLabel}. Clique em "Novo item" ou "Importar lista".`
                : "Tente outro termo de busca."}
            </Typography>
          </div>
        ) : (
          folders.map((f) => {
            const list = byFolder[f];
            const isOpen = isSearching || !!openFolders[f];
            return (
              <div key={f}>
                <div className={classes.folderRow} onClick={() => toggleFolder(f)}>
                  {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                  <FolderOutlinedIcon fontSize="small" color="action" />
                  <span className={classes.folderName}>{f}</span>
                  <Chip size="small" variant="outlined" label={list.length} />
                </div>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  {list.map((it) => (
                    <div key={it.id} className={classes.itemRow}>
                      <div className={classes.itemText}>
                        <div className={classes.itemName}>
                          {it.displayName}
                          {!it.isActive && (
                            <Chip
                              size="small"
                              label="inativo"
                              style={{ marginLeft: 8, height: 18 }}
                            />
                          )}
                        </div>
                        {(it.posology || it.shortName) && (
                          <div className={classes.itemMeta}>
                            {it.posology ? it.posology : it.shortName}
                          </div>
                        )}
                      </div>
                      <div className={classes.itemActions}>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(it)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" onClick={() => setDeleteTarget(it)}>
                            <DeleteOutlineIcon fontSize="small" style={{ color: "#d32f2f" }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </Collapse>
              </div>
            );
          })
        )}
      </Paper>

      {/* Criar / editar item */}
      <Dialog open={dialogOpen} onClose={() => (!saving ? setDialogOpen(false) : null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{form.id ? "Editar item" : "Novo item"}</DialogTitle>
        <DialogContent>
          <div className={classes.dialogFields}>
            <TextField
              label="Pasta"
              value={form.folder}
              onChange={setF("folder")}
              placeholder='ex: Geral, Emagrecedor, Reposição...'
              variant="outlined"
              size="small"
              fullWidth
              helperText={
                existingFolders.length
                  ? `Existentes: ${existingFolders.slice(0, 8).join(", ")}${existingFolders.length > 8 ? "…" : ""}`
                  : "Deixe em branco pra cair em Geral"
              }
            />
            <TextField
              label="Nome de exibição *"
              value={form.displayName}
              onChange={setF("displayName")}
              placeholder="ex: Fórmula com Vitamina D3 / 10.000 UI em Cápsulas"
              variant="outlined"
              size="small"
              fullWidth
              required
            />
            <TextField
              label="Nome curto / comercial (opcional)"
              value={form.shortName}
              onChange={setF("shortName")}
              placeholder="ex: D3 10.000"
              variant="outlined"
              size="small"
              fullWidth
            />
            <TextField
              label="Posologia padrão (opcional)"
              value={form.posology}
              onChange={setF("posology")}
              placeholder="ex: 1 cápsula ao dia, em jejum"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Conteúdo da fórmula (opcional — composição completa)"
              value={form.body}
              onChange={setF("body")}
              placeholder={"Cole a fórmula completa (componentes + dosagens + posologia).\nEx:\nGarcinia camboja ...... 250mg\nCromo (Picolinato) .... 300mcg\nPosologia: 1 dose antes do almoço e do jantar."}
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={4}
            />
          </div>
          <DialogContentText style={{ fontSize: 12, marginTop: 8 }}>
            Receituário de <b>{scopeLabel}</b>. Se o <b>conteúdo da fórmula</b> estiver preenchido, ele entra inteiro na receita; senão, entra o nome (+ posologia).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button color="primary" variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Importar em massa */}
      <Dialog open={importOpen} onClose={() => (!importing ? setImportOpen(false) : null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Importar lista — {scopeLabel}</DialogTitle>
        <DialogContent>
          <div className={classes.dialogFields}>
            <TextField
              label="Pasta (todos os itens vão pra ela)"
              value={importFolder}
              onChange={(e) => setImportFolder(e.target.value)}
              placeholder="ex: Geral"
              variant="outlined"
              size="small"
              fullWidth
            />
            <TextField
              label="Itens (um por linha)"
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder={"Um item por linha. Formato:\nNome de exibição (nome curto)\nMelatonina 3 mg (Importado)"}
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={8}
            />
          </div>
          <DialogContentText style={{ fontSize: 12, marginTop: 8 }}>
            O texto entre parênteses no fim de cada linha vira o "nome curto".
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)} disabled={importing}>
            Cancelar
          </Button>
          <Button color="primary" variant="contained" onClick={handleImport} disabled={importing}>
            {importing ? "Importando..." : "Importar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar exclusão */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} fullScreen={isMobile}>
        <DialogTitle>Remover item</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remover <b>{deleteTarget && deleteTarget.displayName}</b> do receituário? A ação fica registrada na trilha de auditoria.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} style={{ color: "#d32f2f" }}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Receituario;
